import SitesList from '../../components/SitesList/SitesList';
import SitesMap from '../../components/SitesMap/SitesMap';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useEffect, useRef, useState } from 'react';
import { post } from '../../lib/utils/axiosFetcher';
import { setModalData, showModal } from '../../features/modalSlice';
import { TextField, RichTextField, Text } from '@sitecore-jss/sitecore-jss-react';
import { useMutation } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { SelectedSiteMetaData, siteSelection } from '../../features/siteSelectionSlice';
import { showLoader, hideLoader } from '../../features/loaderSlice';
import ToggleViewPanel from '../../components/shared/ToggleViewPanel/ToggleViewPanel';
import { RootState } from '../../store/store';
import { StDesc, StNoSites, StTitle } from './SiteSelection.styles';
import { getSiteDataPayload, transformSiteSelectionData } from './helper';
import { DEFAULT_PRIMARY_SEMIBOLD_FONT } from 'theme/fontDefaults';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import { getKIElementsDecisionData } from '../../lib/utils/kiElements';
import { ISelectedSiteProps } from './SiteSelection.types';
import * as cdpService from '../../services/CdpService';
import {
  fetchNextPossiblePathAndData,
  IUpdateCurrentJourneyPayload,
} from '../../services/CdpService';
import { storeCDPData } from '../../features/cdpDataSlice';
import { useRouter } from 'next/router';
import { post as cdpPost } from '../../lib/utils/axiosCDPFetcher';
import {
  JOURNEY_STEP,
  NSC_STATUS,
  DEFAULT_ZIPCODE_ERROR_MESSAGE,
  DEFAULT_ZIPCODE_INVALID_MESSAGE,
  DEFAULT_ZIPCODE_REQUIRED_MESSAGE,
  DEFAULT_SEARCH_BUTTON_TEXT,
  MOCK_FOV_SCHEDULING_URL,
  MOCK_SUCCESS_URL,
  JOURNEYS,
  CDP_EVENTS,
  FOV_APPOINTMENT_STATUS,
  FOV_SOURCE,
} from '../../constants';
import {
  getCookie,
  ENROLLMENT_EXPIRY_TIME_FALLBACK,
  getCookieExpiryTime,
  getOrCreateEnrollmentId,
} from '../../lib/utils/enrollmentUtil';
import { updateZipcode } from '../../features/geolocationSlice';
import getQueryParamsAsString from '../../lib/utils/getQueryParamsAsString';
import { StTextField } from '../../components/StudyLocation/StudyLocation.style';
import { Controller, useForm } from 'react-hook-form';
import {
  setFOVEPrId,
  setFOVRedirectUrl,
  setAvailableDays,
  setFOVHasSlots,
  setFOVTimezone,
} from '../../features/fovSchedulingSlice';
import {
  bookAppointment,
  getAvailableSlots,
  getCurrentDateTimeISO,
  updateCDPAppointmentStatus,
} from '../../components/FOVScheduling/FOVScheduling.api';
import { getQuery } from '../../lib/utils/common';
import { encryptEPrId } from '../../lib/utils/fovToken';
import { setCookie } from 'cookies-next';
import { trackEvent } from '../../lib/utils/gtmService';
import RichTextWithStudyPnMergeTag from '../../components/shared/RichTextWithStudyPnMergeTag/RichTextWithStudyPnMergeTag';
import { getStateFromZipcode } from '../../lib/utils/contactForm';
import useSiteSelectionHelper from '../../lib/hooks/useSiteSelectionHelper';
import { Box } from '@mui/system';
import isClinicalTrialSite, { isMegaScreenerStudy } from '../../lib/utils/siteHelper';
import ButtonPrimary from '../../components/shared/Button/PrimaryButton';
import useApplyV2Theme from '../../lib/hooks/useApplyV2Theme';
import { UIButton } from '../../components/shared/UIButton.styles';
import getReferralId from 'lib/utils/getReferralId';

// ─── FOV Scheduling URL helpers ───────────────────────────────────────────────

/** Safely extract href from any Sitecore link field shape. */
const getSitecoreLinkHref = (field: any): string => {
  if (!field) return '';
  const v = field.value ?? field;
  if (typeof v === 'string') return v;
  return v?.href || v?.url || '';
};

const SiteSelection: React.FC<any> = (props) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { sitecoreContext } = useSitecoreContext();
  const siteSettings: any = sitecoreContext?.siteSettings;
  const {
    enableSponsorConsentKiElement,
    invitedSiteQueryParamName,
    invitedProtocolQueryParamName,
  } = siteSettings || {};
  const router = useRouter();
  const { query } = router;
  const isTablet = useMediaQuery(theme.breakpoints.between(500, 'lg'));
  const isMobile = useMediaQuery(theme.breakpoints.down(500));
  const selectedPlace = useSelector((state: RootState) => state.selectedSite);
  const potentialParticipant_ID = useSelector(
    (state: RootState) => state.potentialParticipantDetails.potentialParticipant_ID
  );
  const siteName = sitecoreContext?.site?.name;
  const googleMapsLanguage = isClinicalTrialSite(siteName as string)
    ? getCookie('language') || 'en'
    : sitecoreContext?.language || 'en';
  const geolocation = useSelector((state: RootState) => state.geolocation);
  const cdpState = useSelector((state: RootState) => state.cdpData);
  const potentialId = useSelector(
    (state: RootState) => state.potentialParticipantDetails.potentialId
  );

  const queryParamState = useSelector((state: RootState) => state.queryParams);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const contactInfo = useSelector((state: RootState) => state.contactInfoDetails.contactInfo);
  const sitesDataFromRedux = cdpState?.sitesData || [];
  const {
    sitesToShow: data,
    shouldShowViewMore,
    viewAllSites,
    toggleActivePanel,
    activePannel,
    groupTwoSites,
    viewGroupOneSites,
    enableSingleScreen,
    showViewButton,
    showViewButtonFirstScreen,
    showViewButtonSecondScreen,
    resetActivePanelForWfOne,
  } = useSiteSelectionHelper(sitesDataFromRedux, cdpState);

  const { isToapplyV2Theme } = useApplyV2Theme();

  const isDefaultSiteSelected = useRef<boolean>(false);
  const lastSelectedSite = useRef<{ type: string; siteCode: string | number }>({
    type: '',
    siteCode: '',
  });
  //const [enabled, setEnabled] = useState(false);
  const kieConsentRef = useRef<null | string>(null);
  const fovCdpStatusPushedRef = useRef<boolean>(false);
  const fovHasSlotsResolvedRef = useRef<boolean | null>(null);
  const fovSlotsFetchRef = useRef<{
    siteId: string;
    protocolId: string;
    promise: Promise<boolean>;
  } | null>(null);
  const { toggleOptions, nscModalPup, kiElementsModelPopup, SearchPlaceholderText, zipCodeRegex } =
    props?.fields || {};
  const errorMessage = props?.fields?.errorMessage || DEFAULT_ZIPCODE_ERROR_MESSAGE;
  const searchButtonText = props?.fields?.searchButtonText || DEFAULT_SEARCH_BUTTON_TEXT;
  const requiredValidationMessage =
    props?.fields?.requiredValidationMessage || DEFAULT_ZIPCODE_REQUIRED_MESSAGE;
  const errorValidationMessage =
    props?.fields?.errorValidationMessage || DEFAULT_ZIPCODE_INVALID_MESSAGE;
  const tenantKey = isMegaScreenerStudy(siteSettings)
    ? getCookie('tenantKey') || ''
    : siteSettings?.tenantKey?.value || '';
  const journeyType = useSelector((state: RootState) => state?.cdpData?.journeyType);
  const description = props.fields?.description;
  const title = props?.fields?.title;
  const removeQueryParamList = (siteSettings as any)?.RemoveQueryParamList?.value?.split('&');
  const retainQueryParamList = (siteSettings as any)?.RetainQueryParamList?.value?.split('&');

  const getSuccessUrlBasedOnPatientType = (): string => {
    const patientType = cdpState?.patientType;
    switch (patientType) {
      case 'adult':
        return getQueryParamsAsString() === ''
          ? `${siteSettings?.adultSiteUrls?.fields?.successUrl?.value?.href}`
          : `${
              siteSettings?.adultSiteUrls?.fields?.successUrl?.value?.href
            }${getQueryParamsAsString()}`;
      case 'minor':
        return getQueryParamsAsString() === ''
          ? `${siteSettings?.minorSiteUrls?.fields?.successUrl?.value?.href}`
          : `${
              siteSettings?.minorSiteUrls?.fields?.successUrl?.value?.href
            }${getQueryParamsAsString()}`;
      case 'caregiver':
        return getQueryParamsAsString() === ''
          ? `${siteSettings?.caregiverSiteUrls?.fields?.successUrl?.value?.href}`
          : `${
              siteSettings?.caregiverSiteUrls?.fields?.successUrl?.value?.href
            }${getQueryParamsAsString()}`;
      default:
        return getQueryParamsAsString() === ''
          ? `${siteSettings?.adultSiteUrls?.fields?.successUrl?.value?.href}`
          : `${
              siteSettings?.adultSiteUrls?.fields?.successUrl?.value?.href
            }${getQueryParamsAsString()}`;
    }
  };

  /**
   * Resolves the FOV Scheduling page URL from Sitecore site settings.
   * Priority: patient-type siteUrls.fovSchedulingUrl → top-level siteSettings.fovSchedulingUrl
   *           → env var NEXT_PUBLIC_FOV_SCHEDULING_URL → MOCK_FOV_SCHEDULING_URL
   */
  const getFovSchedulingUrl = (): string => {
    const patientType = cdpState?.patientType;
    let url = '';

    if (patientType === 'minor') {
      url = getSitecoreLinkHref(siteSettings?.minorSiteUrls?.fields?.schedulingUrl);
    } else if (patientType === 'caregiver') {
      url = getSitecoreLinkHref(siteSettings?.caregiverSiteUrls?.fields?.schedulingUrl);
    } else {
      url = getSitecoreLinkHref(siteSettings?.adultSiteUrls?.fields?.schedulingUrl);
    }

    return url || MOCK_FOV_SCHEDULING_URL;
  };

  /**
   * Resolves the success (redirect) URL for FOV Scheduling.
   * Falls back to MOCK_SUCCESS_URL if Sitecore hasn't been configured.
   */
  const getFovSuccessUrl = (): string => {
    const url = getSuccessUrlBasedOnPatientType();
    return !url || url === 'undefined' ? MOCK_SUCCESS_URL : url;
  };

  /**
   * Builds the full FOV Scheduling redirect URL.
   */
  const buildFovSchedulingRedirectUrl = async (
    baseUrl: string,
    ePrId: string,
    successRedirectUrl: string,
    includeIntegratedType = false
  ): Promise<{ pathname: string; query: Record<string, string | string[]> }> => {
    // Encrypt only the ePrId — the rest stay as plain query params.
    const encryptedEPrId = await encryptEPrId(ePrId);

    const fovQuery: Record<string, string | string[]> = {
      ...getQuery(query, retainQueryParamList, removeQueryParamList),
      ePrId: encryptedEPrId,
      RedirectUrl: successRedirectUrl,
    };
    if (includeIntegratedType) fovQuery.type = 'integrated';

    return { pathname: baseUrl, query: fovQuery };
  };

  const fetchSlotsForSite = (siteId: string, protocolId: string): Promise<boolean> => {
    const cached = fovSlotsFetchRef.current;
    if (cached && cached.siteId === siteId && cached.protocolId === protocolId) {
      return cached.promise;
    }

    const promise = (async (): Promise<boolean> => {
      try {
        const currentDateTime = getCurrentDateTimeISO();
        const slotsRes = await getAvailableSlots({
          siteId: siteId,
          protocolId,
          currentDateTime,
        });
        const hasSlots = !!(slotsRes?.hasSlots && slotsRes?.days?.length);
        dispatch(setAvailableDays(hasSlots ? slotsRes.days : []));
        dispatch(setFOVHasSlots(hasSlots));
        dispatch(setFOVTimezone(slotsRes?.timezone ?? null));
        return hasSlots;
      } catch {
        // Non-blocking — treat as no slots
        dispatch(setAvailableDays([]));
        dispatch(setFOVHasSlots(false));
        return false;
      }
    })();

    fovSlotsFetchRef.current = { siteId, protocolId, promise };
    return promise;
  };

  const resolveFovSlotsForConfirm = async (): Promise<boolean> => {
    const siteId = selectedPlace?.siteInfo?.siteId || '';
    const protocolId = selectedPlace?.siteInfo?.protocol || '';
    const cached = fovSlotsFetchRef.current;

    if (cached && cached.siteId === siteId && cached.protocolId === protocolId) {
      return cached.promise;
    }

    return fetchSlotsForSite(siteId, protocolId);
  };

  const prepareFovCdpStatusBeforeSave = async (payload: ISelectedSiteProps): Promise<void> => {
    // Reset for this confirm attempt.
    fovCdpStatusPushedRef.current = false;
    fovHasSlotsResolvedRef.current = null;

    const siteInfo = payload?.selectedPlace?.siteInfo;
    const isFovConfirm =
      payload?.selectedPlace?.selectionType === 'Confirm' &&
      !!siteInfo?.isFovSchedulingEnabled &&
      !!journeyType &&
      journeyType !== JOURNEYS.JOURNEY_01;

    if (!isFovConfirm) return;

    const earlyEPrId: string = getReferralId(getOrCreateEnrollmentId(), siteInfo?.siteCode);
    const isSiteDirected = siteInfo?.siteDirectedSchedule ?? false;

    // ── Site-directed: push "siteDirectedScheduling" up-front ──────────────────
    if (isSiteDirected) {
      try {
        await updateCDPAppointmentStatus({
          ePrId: earlyEPrId,
          appointmentStatus: FOV_APPOINTMENT_STATUS.SITE_DIRECTED_SCHEDULING,
          source: FOV_SOURCE,
        });
        fovCdpStatusPushedRef.current = true;
      } catch {
        // non-blocking — continue with the save/fetch regardless
      }
      return;
    }

    // ── Slot-based: resolve slots; push "noSlotsAvailableForSubject" when none ──
    const hasSlots = await resolveFovSlotsForConfirm();
    fovHasSlotsResolvedRef.current = hasSlots;

    if (!hasSlots) {
      try {
        await updateCDPAppointmentStatus({
          ePrId: earlyEPrId,
          appointmentStatus: FOV_APPOINTMENT_STATUS.NO_SLOTS_AVAILABLE_FOR_SUBJECT,
          source: FOV_SOURCE,
        });
        fovCdpStatusPushedRef.current = true;
      } catch {
        // non-blocking — continue with the save/fetch regardless
      }
    }
  };

  const updateCurrentJourneyStepinCDP = async () => {
    const payload: IUpdateCurrentJourneyPayload = {
      enrollmentId: getOrCreateEnrollmentId(),
      journeyStep: JOURNEY_STEP.SITESELECTION,
      journeyStatus: '',
      isActiveEnrollment: '1',
    };

    if (potentialParticipant_ID) {
      const guestType = cdpState?.guestType || 'visitor';
      cdpService.updateCurrentJourneyInCDP(potentialParticipant_ID, guestType, payload);
    }
  };

  const updateZipcodeReferralinCDP = async (
    zipCode: string,
    state: string | null,
    referralStatus: string,
    isNsc: boolean
  ) => {
    const payload = {
      enrollmentId: getOrCreateEnrollmentId(),
      zipCode: zipCode,
      patientState: state,
      journeyStep: JOURNEY_STEP.SITESELECTION,
      patientReferralStatus: referralStatus,
      patientType: cdpState?.patientType,
    };
    if (potentialParticipant_ID) {
      const URL = `/api/contacts/UpdateZipcodeReferral/${potentialParticipant_ID}?guestType=${
        cdpState?.guestType || 'visitor'
      }`;
      try {
        if (isNsc) {
          setIsRedirecting(true);
        }
        await cdpPost(URL, payload)
          .then(async () => {
            const experienceId = process.env.NEXT_PUBLIC_CDP_EXPERIENCE_SITESELECTION_ID;
            const data = await fetchNextPossiblePathAndData(
              experienceId,
              isNsc,
              false,
              sitecoreContext?.site?.name
            );
            //storing cdp data, so can be used in next step
            // setLoading(false);

            //Added for workflow1 virtual site
            if (cdpState.journeyType === JOURNEYS.JOURNEY_01 && data?.isVirtual) {
              const selectedSitesData = data?.sitesData[0];

              await cdpService.updateIsVirtualinCDP(
                getCookie('bx_guest_ref'),
                cdpState?.guestType || 'visitor', // TODO: Confirm if it is Visitor or customer
                data?.isHumanAPIEnabledForVirtual,
                data?.sitesData,
                potentialId,
                potentialParticipant_ID,
                ''
              );

              const { isKIEenabled } = getKIElementsDecisionData(
                enableSponsorConsentKiElement,
                selectedSitesData.isProtocalKiElementsEnabled,
                selectedSitesData.isSiteKiElementsEnabled
              );

              const virtualSitesData: SelectedSiteMetaData = {
                selectionType: 'Confirm',
                shouldInfoWindowVisible: true,
                siteInfo: selectedSitesData,
              };
              dispatch(siteSelection(virtualSitesData));
              saveSelectedSiteData({
                selectedPlace: virtualSitesData,
                isKIEenabled,
                isSponserKieEnabled: enableSponsorConsentKiElement?.value,
                isUserOptedKiElementConsent: null,
              });

              setCookie('journeyStep', JOURNEY_STEP.SITESELECTION, {
                expires: getCookieExpiryTime(
                  siteSettings?.resumeEnrollmentExpirationDays?.value ||
                    ENROLLMENT_EXPIRY_TIME_FALLBACK
                ),
                secure: true,
              });
              dispatch(storeCDPData(data));
              if (data?.nextStepUrl?.includes('siteselectionsuccess')) {
                const cookie_expiry = new Date();
                cookie_expiry.setDate(cookie_expiry.getDate() + 1);
                setCookie('allowSiteSelectionSuccess', true, {
                  secure: true, // optional: ensures it's sent only over HTTPS
                  expires: cookie_expiry,
                });
              }
              await router.push(
                {
                  pathname: data?.nextStepUrl,
                  query: getQuery(query, retainQueryParamList, removeQueryParamList),
                },
                undefined,
                { scroll: false } // 👈 prevent default scroll behavior
              );
            } else {
              isDefaultSiteSelected.current = false;
              dispatch(hideLoader());
              dispatch(storeCDPData(data));
              if (!data?.sitesData?.length && data?.nextStepUrl) {
                setCookie('journeyStep', JOURNEY_STEP.SITESELECTION, {
                  expires: getCookieExpiryTime(siteSettings?.resumeEnrollmentExpirationDays?.value),
                  secure: true,
                });
                if (data?.nextStepUrl?.includes('siteselectionsuccess')) {
                  const cookie_expiry = new Date();
                  cookie_expiry.setDate(cookie_expiry.getDate() + 1);
                  setCookie('allowSiteSelectionSuccess', true, {
                    secure: true, // optional: ensures it's sent only over HTTPS
                    expires: cookie_expiry,
                  });
                }
                router.replace(
                  {
                    pathname: data?.nextStepUrl,
                    query: getQuery(query, retainQueryParamList, removeQueryParamList),
                  },
                  undefined
                );
              }
            }
            //End of Added for workflow1 virtual site
          })
          .finally(() => {
            if (isNsc) {
              setIsRedirecting(false);
            }
          });
      } catch (_e) {
        // setLoading(false);
      }
    }
  };

  const handleClickOnNSCLink = () => {
    const data = {
      title: nscModalPup?.fields?.modalTitle,
      description: nscModalPup?.fields?.modalDescription,
      customButtons: nscModalPup?.fields?.modalCTAButtons,
      hideCloseIcon: true,
      continueButtonCallback: handleNSCContinueClick,
    };
    dispatch(setModalData(data));
    dispatch(showModal());
  };

  useEffect(() => {
    updateCurrentJourneyStepinCDP();
    trackEvent(
      {
        event: 'Landed on Site selection page',
      },
      isClinicalTrialSite(siteName as string) ? true : false
    );
  }, []);

  useEffect(() => {
    //setLoading(false);
    if (!isDefaultSiteSelected.current && Array.isArray(data) && data.length > 0) {
      const firstSiteFromList = data[0];
      const payload = {
        siteInfo: firstSiteFromList,
        selectionType: 'Partial',
        shouldInfoWindowVisible: true,
      };
      dispatch(siteSelection(payload));
      const extensionData = {
        protocol: (firstSiteFromList as any)?.protocol,
        isHybridSite: (firstSiteFromList as any)?.isHybridSite,
        siteCode: (firstSiteFromList as any)?.siteCode,
      };
      cdpService.event(CDP_EVENTS.SITE_SELECTION_TOGGLE, extensionData);

      isDefaultSiteSelected.current = true;
    }
  }, [data]);

  const saveSiteSelection = async (payload: ISelectedSiteProps) => {
    //TODO: get api and payload from backend team
    lastSelectedSite.current = {
      type: payload?.selectedPlace?.selectionType,
      siteCode: payload?.selectedPlace?.siteInfo?.siteCode,
    };
    const reqBody = getSiteDataPayload(
      payload,
      contactInfo,
      tenantKey,
      getOrCreateEnrollmentId(),
      potentialParticipant_ID,
      potentialId,
      '',
      journeyType
    );
    reqBody.siteInfo.journeyType = journeyType;
    dispatch(showLoader());
    return new Promise(async (resolve, reject) => {
      try {
        // FOV: push the CDP appointment status BEFORE saving the site selection so
        // the later fetchNextPossiblePathAndData reads the correct status.
        await prepareFovCdpStatusBeforeSave(payload);
        const response = await post('/SiteSelection/AddOrUpdatePatientSiteDetails', reqBody);
        resolve({
          response: response,
          reqBody,
          payload: {
            ...reqBody,
            ...selectedPlace?.siteInfo,
            patientReferralStatus: cdpState?.patientReferralStatus,
            isSiteKiElementsEnabled: payload?.selectedPlace?.siteInfo?.isSiteKiElementsEnabled,
            isProtocolKiElementsEnabled:
              payload?.selectedPlace?.siteInfo?.isProtocalKiElementsEnabled,
            piName: selectedPlace?.siteInfo?.piName,
            eConsentUrl: selectedPlace?.siteInfo?.eConsentUrl,
            emailCras: selectedPlace?.siteInfo?.emailCras,
            studyStaffEmails: selectedPlace?.siteInfo?.studyStaffEmails,
            status: selectedPlace?.siteInfo?.siteStatus,
            allowEmails: selectedPlace?.siteInfo?.allowEmails,
            allowSms: selectedPlace?.siteInfo?.allowSms,
            latitude: selectedPlace?.siteInfo?.latitude,
            longitude: selectedPlace?.siteInfo?.longitude,
            radius: selectedPlace?.siteInfo?.siteRadius,
          },
        });
      } catch (error) {
        reject({ error: error, payload: reqBody });
      }
    });
  };

  const addUpdateSiteProtocolToDataExtension = async (data: any): Promise<any> => {
    const transformedData = transformSiteSelectionData(data);
    // If journey type is JOURNEY_01, set patient referral status to Abandoned
    if (transformedData && journeyType === JOURNEYS.JOURNEY_01) {
      transformedData.patientReferralStatus = 'Abandoned';
    }
    const guestRef = getCookie('bx_guest_ref');
    const URL = `/api/contacts/AddUpdateSiteProtocolToDataExtension/${guestRef}?guestType=${
      cdpState?.guestType || 'customer'
    }`;
    try {
      const res = await cdpPost(URL, transformedData);
      return res;
    } catch (error: any) {
      throw error;
    }
  };

  const { mutate: saveSelectedSiteData } = useMutation(saveSiteSelection, {
    onError: async () => {
      const extensionData = {
        tenantKey: tenantKey,
        journeyType: journeyType as string,
        selectedSite: lastSelectedSite.current.siteCode,
      };
      cdpService.event(CDP_EVENTS.SITE_SELECTION_SITE_SELECTED_ERROR, extensionData);

      lastSelectedSite.current = { type: '', siteCode: '' };
      dispatch(hideLoader());
    },
    onSuccess: async ({ payload }: any) => {
      const extensionData = {
        protocol: selectedPlace?.siteInfo?.protocol,
        isHybridSite: selectedPlace?.siteInfo?.isHybridSite,
        siteCode: selectedPlace?.siteInfo.siteCode,
      };

      // Post CDP event data and redirect on completion
      if (selectedPlace.selectionType === 'Confirm') {
        //Navigate as per CDP response
        //TODO: handle negative response
        try {
          //setIsRedirecting(true);
          const retVal: any = await addUpdateSiteProtocolToDataExtension(payload);

          const experienceId = process.env.NEXT_PUBLIC_CDP_EXPERIENCE_SITESELECTION_ID;
          if (retVal?.data === true) {
            setCookie('journeyStep', JOURNEY_STEP.SITESELECTION, {
              expires: getCookieExpiryTime(
                siteSettings?.resumeEnrollmentExpirationDays?.value ||
                  ENROLLMENT_EXPIRY_TIME_FALLBACK
              ),
              secure: true,
            });
            // Add retry mechanism for fetchNextPossiblePathAndData
            let nextStepData;
            const MAX_EXPERIENCE_RETRY_COUNT = 4;
            const retryAttempts = 0;
            while (retryAttempts < MAX_EXPERIENCE_RETRY_COUNT) {
              try {
                nextStepData = await fetchNextPossiblePathAndData(
                  experienceId,
                  false,
                  true,
                  sitecoreContext?.site?.name
                );
                break; // Success, exit loop
              } catch (e: any) {
                if (retryAttempts >= MAX_EXPERIENCE_RETRY_COUNT) {
                  throw new Error('fetchNextSteps failed'); // Exit the loop if all retries fail
                }

                // Wait for 2 second before retrying
                await new Promise((resolve) => setTimeout(resolve, 2000));
              }
            }
            //storing cdp data, so can be used in next step
            if (nextStepData?.nextStepUrl?.includes('siteselectionsuccess')) {
              const cookie_expiry = new Date();
              cookie_expiry.setDate(cookie_expiry.getDate() + 1);
              setCookie('allowSiteSelectionSuccess', true, {
                secure: true, // optional: ensures it's sent only over HTTPS
                expires: cookie_expiry,
              });
            }
            if (nextStepData?.nextStepUrl) {
              // ── FOV Scheduling flow ────────────────────────────────────────────────
              if (
                selectedPlace?.siteInfo?.isFovSchedulingEnabled &&
                journeyType &&
                journeyType !== JOURNEYS.JOURNEY_01
              ) {
                const siteEPrId: string = getReferralId(
                  getOrCreateEnrollmentId(),
                  selectedPlace?.siteInfo?.siteCode
                );
                const isSiteDirected = selectedPlace?.siteInfo?.siteDirectedSchedule ?? false;

                if (isSiteDirected) {
                  // ── Site-directed: CDP status was already pushed before the save
                  //    (prepareFovCdpStatusBeforeSave). Skip the duplicate CDP update
                  //    when already done; always fire the DAP booking regardless.
                  if (!fovCdpStatusPushedRef.current) {
                    try {
                      await updateCDPAppointmentStatus({
                        ePrId: siteEPrId,
                        appointmentStatus: FOV_APPOINTMENT_STATUS.SITE_DIRECTED_SCHEDULING,
                        source: FOV_SOURCE,
                      });
                    } catch {
                      // non-blocking — navigate regardless
                    }
                  }
                  bookAppointment({
                    ePrId: siteEPrId,
                    appointmentStatus: FOV_APPOINTMENT_STATUS.SITE_DIRECTED_SCHEDULING,
                    source: FOV_SOURCE,
                  });
                  await router.replace(
                    {
                      pathname: nextStepData?.nextStepUrl,
                      query: getQuery(query, retainQueryParamList, removeQueryParamList),
                    },
                    undefined
                  );
                  dispatch(hideLoader());
                  return;
                } else {
                  // ── Slot-based: use pre-fetched data from Redux ────────────────
                  const successUrl: string = getFovSuccessUrl();
                  const fovSchedulingUrl: string = getFovSchedulingUrl();
                  // Reuse the result resolved before the CDP fetch when available,
                  // otherwise resolve now (keeps a single GetAvailableSlots call).
                  const hasSlots =
                    fovHasSlotsResolvedRef.current ?? (await resolveFovSlotsForConfirm());

                  if (!hasSlots) {
                    // ── No slots available ─────────────────────────────────────
                    if (!fovCdpStatusPushedRef.current) {
                      try {
                        await updateCDPAppointmentStatus({
                          ePrId: siteEPrId,
                          appointmentStatus: FOV_APPOINTMENT_STATUS.NO_SLOTS_AVAILABLE_FOR_SUBJECT,
                          source: FOV_SOURCE,
                        });
                      } catch {
                        // non-blocking
                      }
                    }
                    bookAppointment({
                      ePrId: siteEPrId,
                      appointmentStatus: FOV_APPOINTMENT_STATUS.NO_SLOTS_AVAILABLE_FOR_SUBJECT,
                      source: FOV_SOURCE,
                    });
                    await router.replace(
                      {
                        pathname: nextStepData?.nextStepUrl,
                        query: getQuery(query, retainQueryParamList, removeQueryParamList),
                      },
                      undefined
                    );
                    dispatch(hideLoader());
                    return;
                  }

                  // ── Slots available ────────────────────────────────────────────
                  dispatch(setFOVEPrId(siteEPrId));
                  dispatch(setFOVRedirectUrl(successUrl));

                  // Book "initiated" status before redirect (all journeys)
                  try {
                    await updateCDPAppointmentStatus({
                      ePrId: siteEPrId,
                      appointmentStatus: FOV_APPOINTMENT_STATUS.INITIATED,
                      source: FOV_SOURCE,
                    });
                  } catch {
                    // non-blocking
                  }
                  bookAppointment({
                    ePrId: siteEPrId,
                    appointmentStatus: FOV_APPOINTMENT_STATUS.INITIATED,
                    source: FOV_SOURCE,
                  });

                  const schedulingTarget = await buildFovSchedulingRedirectUrl(
                    fovSchedulingUrl,
                    siteEPrId,
                    successUrl,
                    true
                  );
                  await router.replace(schedulingTarget, undefined);
                  dispatch(hideLoader());
                  return;
                }
              }
              if (nextStepData?.nextStepUrl?.includes('siteselectionsuccess')) {
                const cookie_expiry = new Date();
                cookie_expiry.setDate(cookie_expiry.getDate() + 1);
                setCookie('allowSiteSelectionSuccess', true, {
                  secure: true, // optional: ensures it's sent only over HTTPS
                  expires: cookie_expiry,
                });
              }
              await router.replace(
                {
                  pathname: nextStepData?.nextStepUrl,
                  query: getQuery(query, retainQueryParamList, removeQueryParamList),
                },
                undefined
              );
            }
            dispatch(storeCDPData(nextStepData));
          }
        } catch (_e: any) {
          //TODO: CDP call failed need to check
        }
        cdpService.event(CDP_EVENTS.SITE_SELECTION_SITE_SELECTED, extensionData);
      } else {
        // Saving siteinfo data to cdp on toggle site also
        await addUpdateSiteProtocolToDataExtension(payload)
          .then(() => {
            cdpService.event(CDP_EVENTS.SITE_SELECTION_TOGGLE, extensionData);
          })
          .catch(() => {
            const errorData = {
              tenantKey: tenantKey,
              journeyType: journeyType as string,
              selectedSite: lastSelectedSite.current?.siteCode,
            };
            cdpService.event(CDP_EVENTS.SITE_SELECTION_SITE_SELECTED_ERROR, errorData);
          });
      }
      dispatch(hideLoader());
    },
  });

  const updateNoSiteConvenientForPatient = (data: any) => {
    const { tenantKey, enrollmentId, referralStatus } = data;
    setIsRedirecting(true);

    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await post(
          `/SiteSelection/UpdatePatientDetailsForNSC?potentialId=${potentialId}&tenantKey=${tenantKey}&enrollmentId=${enrollmentId}&patientReferralStatus=${referralStatus}`,
          {}
        );
        resolve({
          data,
          payload: {},
        });
      } catch (error: any) {
        reject({ error: error, payload: {} });
      } finally {
        setIsRedirecting(false);
      }
    });
  };

  const { mutate: nscMutate } = useMutation(updateNoSiteConvenientForPatient, {
    onError: () => {
      cdpService.event(CDP_EVENTS.SITE_SELECTION_NO_SITE_CONVENIENT_ERROR, {
        tenantKey: tenantKey,
        journeyType: journeyType as string,
      });
    },
    onSuccess: async () => {
      const extensionData = {
        referralStatus: '',
      };
      const enrollmentId: any = getOrCreateEnrollmentId();
      try {
        await cdpPost(
          `/api/contacts/UpdateSiteProtocolDetailsForNSC/${getCookie(
            'bx_guest_ref'
          )}?enrollmentId=${enrollmentId}`,
          {}
        );
      } catch (_error) {}
      cdpService.event(CDP_EVENTS.SITE_SELECTION_NO_SITE_CONVENIENT, extensionData);

      try {
        let referralStatus =
          queryParamState[invitedSiteQueryParamName?.value] &&
          invitedSiteQueryParamName?.value &&
          queryParamState[invitedProtocolQueryParamName?.value] &&
          invitedProtocolQueryParamName?.value
            ? NSC_STATUS?.SITENOTCONVENIENT
            : NSC_STATUS?.NOSITECONVENIENT;
        // If journey type is JOURNEY_01, set patient referral status to Abandoned
        if (journeyType === JOURNEYS.JOURNEY_01) {
          referralStatus = 'Abandoned';
        }
        updateZipcodeReferralinCDP('', '', referralStatus, true);
      } catch (_e) {
        //TODO: need to check if exp api fails what need to be done
      }
    },
  });

  const handleNSCContinueClick = () => {
    if (tenantKey && getOrCreateEnrollmentId()) {
      const nscPayload = {
        tenantKey,
        enrollmentId: getOrCreateEnrollmentId(),
        referralStatus:
          queryParamState[invitedSiteQueryParamName?.value] &&
          invitedSiteQueryParamName?.value &&
          queryParamState[invitedProtocolQueryParamName?.value] &&
          invitedProtocolQueryParamName?.value
            ? NSC_STATUS?.SITENOTCONVENIENT
            : NSC_STATUS?.NOSITECONVENIENT,
      };
      // setNscStatus(nscPayload?.referralStatus);
      nscMutate(nscPayload);
    }
  };

  const handleConsents = (isKIEenabled: boolean) => {
    saveSelectedSiteData({
      selectedPlace,
      isKIEenabled,
      isSponserKieEnabled: enableSponsorConsentKiElement?.value,
      isUserOptedKiElementConsent: kieConsentRef.current,
    });

    const extensionData: any = {
      kieConsent: kieConsentRef.current,
    };
    if (isKIEenabled) {
      cdpService.event(CDP_EVENTS.SITE_SELECTION_KIELEMENT_POPUP, extensionData);
    }
  };

  const showKIEModal = () => {
    const data = {
      title: kiElementsModelPopup?.fields?.modalTitle,
      description: kiElementsModelPopup?.fields?.modalDescription,
      customButtons: kiElementsModelPopup?.fields?.modalCTAButtons,
      hideCloseIcon: true,
      continueButtonCallback: () => {
        kieConsentRef.current = 'Yes';
        handleConsents(true);
      },
      cancelButtonCallback: () => {
        kieConsentRef.current = 'No';
        handleConsents(true);
      },
    };
    dispatch(setModalData(data));
    dispatch(showModal());
  };

  //Partial site saving  on site selection (Clicking on card or marker)
  useEffect(() => {
    // IN CASE OF VIRTUAL SITE, WE SHOULD NOT SAVE THE SITE SELECTION FROM HERE
    if (cdpState?.isVirtual) {
      return;
    }
    if (selectedPlace && selectedPlace?.siteInfo?.siteCode) {
      // Check KIE
      const { isKIEenabled, isSponserSpecificConsentEnabled } = getKIElementsDecisionData(
        enableSponsorConsentKiElement,
        selectedPlace?.siteInfo?.isProtocalKiElementsEnabled,
        selectedPlace?.siteInfo?.isSiteKiElementsEnabled
      );
      // if (isKIEenabled && isSponserSpecificConsentEnabled) {
      if (selectedPlace.selectionType === 'Confirm') {
        if (isKIEenabled && isSponserSpecificConsentEnabled) {
          showKIEModal();
        } else {
          // payload fo LL to be added
          saveSelectedSiteData({
            selectedPlace,
            isKIEenabled,
            isSponserKieEnabled: enableSponsorConsentKiElement?.value,
            isUserOptedKiElementConsent: null,
          });
        }
      } else {
        // SITE_SELECTION_TOGGLE
        saveSelectedSiteData({
          selectedPlace,
          isKIEenabled,
          isSponserKieEnabled: enableSponsorConsentKiElement?.value,
          isUserOptedKiElementConsent: null,
        });

        // ── Pre-fetch GetAvailableSlots when switching cards ───────────────────
        if (selectedPlace?.siteInfo?.isFovSchedulingEnabled) {
          if (selectedPlace?.siteInfo?.siteDirectedSchedule) {
            fovSlotsFetchRef.current = null;
            dispatch(setAvailableDays([]));
            dispatch(setFOVHasSlots(false));
          } else {
            // Slot-based card: reset then fire a single background fetch. Clearing
            // the dedupe ref forces a fresh fetch for this card switch; the cached
            // promise is then reused at confirm time (no duplicate GetAvailableSlots).
            dispatch(setFOVHasSlots(false));
            dispatch(setAvailableDays([]));
            fovSlotsFetchRef.current = null;
            fetchSlotsForSite(
              selectedPlace?.siteInfo?.siteId || '',
              selectedPlace?.siteInfo?.protocol || ''
            );
          }
        }
      }
    }
  }, [selectedPlace]);

  const buttonText: TextField = {
    value: props?.fields?.selectButton?.value || '',
  };
  const modalButtonText: TextField = {
    value: props?.fields?.modalButtonText?.value || '',
  };
  const siteNameFormat: RichTextField = {
    value: props?.fields?.siteNameFormat?.value || '',
  };
  const siteNameFormatInMap: RichTextField = {
    value: props?.fields?.siteNameFormatinMap?.value || '',
  };
  const sitePhoneNumber: RichTextField = {
    value: props?.fields?.sitePhoneNumberFormat?.value || '',
  };

  const siteAddressFormat: RichTextField = {
    value: props?.fields?.siteAddressFormat?.value,
  };
  const siteAddressFormatinMap: RichTextField = {
    value: props?.fields?.siteAddressFormatinMap?.value,
  };

  const noSitesAvailable = () => {
    return (
      <Grid
        container
        spacing={0}
        direction="column"
        alignItems="center"
        justifyContent="center"
        sx={{ maxHeight: '550px', height: '550px' }}
      >
        <StNoSites isMobile={isMobile} isTablet={isTablet}>
          {journeyType &&
            journeyType === JOURNEYS.JOURNEY_01 &&
            'Please enter your ZIP code above to find a location near you'}
        </StNoSites>
      </Grid>
    );
  };

  const {
    control,
    getValues,
    handleSubmit,
    setError,
    setValue,
    clearErrors,
    formState: { errors },
  } = useForm({ mode: 'all' });

  // Added to populate zipcode field with geolocation zipcode
  useEffect(() => {
    setValue('zipcode', geolocation?.location?.zipcode);
  }, [geolocation?.location?.zipcode]);

  const numericOnly = (e: any) => {
    if (!/[0-9]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Enter') {
      e.preventDefault();
    }
  };

  const handleZipcodeSearch = handleSubmit(async () => {
    if (errors?.zipcode) {
      setError('zipcode', { type: 'manual', message: errorMessage?.value });
    } else {
      clearErrors('zipcode'); // will clear both errors from test.firstName and test.lastName
      // No errors, proceed with the search.
      dispatch(showLoader());
      const value = getValues();
      let state: null | string = null;
      try {
        state = (await getStateFromZipcode(value?.zipcode)) as string;
      } catch (_e) {}

      if (JOURNEYS.JOURNEY_01 === journeyType) {
        resetActivePanelForWfOne();
        const data: any = { zipcode: value?.zipcode, state: state || null, city: '' };
        dispatch(updateZipcode(data));
      }
      updateZipcodeReferralinCDP(value?.zipcode, state, '', false);
    }
  });

  //TODO: what should happen when no site from backend or any error
  return (
    <Grid container direction="column" gap={2} id="site-selection">
      <Grid item>
        <StTitle isMobile={isMobile} isTablet={isTablet}>
          <RichTextWithStudyPnMergeTag field={title} />
        </StTitle>
      </Grid>
      <Grid item>
        <StDesc isMobile={isMobile} isTablet={isTablet}>
          <RichTextWithStudyPnMergeTag field={description} />
        </StDesc>
      </Grid>
      {journeyType && typeof journeyType === 'string' && journeyType === JOURNEYS.JOURNEY_01 && (
        <Grid item>
          <Stack sx={{ pt: 1 }}>
            <StDesc
              style={{ paddingTop: '20px', paddingBottom: '10px' }}
              isMobile={isMobile}
              isTablet={isTablet}
            >
              <Text field={props?.fields?.SearchHeading} />
            </StDesc>
            {!isMobile && (
              <Stack
                direction="row"
                justifyContent={!isTablet ? 'flex-start' : 'center'}
                alignItems="center"
                spacing={2}
              >
                <Grid width={'225px'}>
                  {/* <TextBox
                    onChangeHandler={handleZipcode}
                    // onKeyDown={numericOnly}
                    placeholder={
                      props?.fields?.SearchPlaceholderText
                        ? props?.fields?.SearchPlaceholderText.value
                        : ''
                    }
                    requiredMessage={requiredValidationMessage}
                    name="zipcode"
                    value={zipcode}
                  /> */}
                  <Controller
                    defaultValue={geolocation?.location?.zipcode}
                    rules={{
                      required: {
                        value: true,
                        message: requiredValidationMessage?.value,
                      },
                      pattern: {
                        // value: /^\d{5}$/,
                        value: new RegExp(zipCodeRegex?.fields?.value?.value),
                        message: errorValidationMessage?.value,
                      },
                    }}
                    control={control}
                    name="zipcode"
                    render={({ field }) => (
                      <>
                        <StTextField
                          sx={{ width: '100%' }}
                          placeholder={SearchPlaceholderText?.value}
                          onKeyDown={numericOnly}
                          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 5 }}
                          error={Boolean(errors?.zipcode)} // Set the error prop based on the presence of an error
                          {...field}
                        />
                        {errors.zipcode && (
                          <Stack style={{ marginTop: '0px' }} alignItems="center" direction="row">
                            <Typography color="error">{`${errors?.zipcode?.message}`}</Typography>
                          </Stack>
                        )}
                      </>
                    )}
                  />
                </Grid>
                <Button
                  className="btn"
                  style={{
                    height: '40px',
                    padding: '12px 22px 12px 22px',
                    borderRadius: '20px',
                    fontFamily: `${
                      theme?.palette?.primaryFontSemiBoldType || DEFAULT_PRIMARY_SEMIBOLD_FONT
                    }, sans-serif`,
                    fontSize: '16px',
                    fontWeight: '600',
                    lineHeight: '16px',
                    textAlign: 'center',
                    textTransform: 'none',
                    maxWidth: '122px',
                    alignSelf: 'baseline',
                  }}
                  variant="contained"
                  onClick={handleZipcodeSearch}
                >
                  {searchButtonText.value}
                </Button>
              </Stack>
            )}

            {isMobile && (
              <Stack direction="column" alignItems="center" spacing={2}>
                <Grid width={isMobile ? '250px' : '225px'}>
                  {/* <TextBox
                    onChangeHandler={handleZipcode}
                    placeholder={
                      props?.fields?.SearchPlaceholderText
                        ? props?.fields?.SearchPlaceholderText.value
                        : ''
                    }
                    defaultValue={geolocation?.location?.zipcode}
                    requiredMessage={requiredValidationMessage}
                    name="zipcode"
                    value={zipcode}
                  /> */}
                  <Controller
                    defaultValue={geolocation?.location?.zipcode}
                    rules={{
                      required: {
                        value: true,
                        message: requiredValidationMessage?.value,
                      },
                      pattern: {
                        value: new RegExp(zipCodeRegex?.fields?.value?.value),
                        message: errorValidationMessage?.value,
                      },
                    }}
                    control={control}
                    name="zipcode"
                    render={({ field }) => (
                      <>
                        <StTextField
                          sx={{ width: '100%' }}
                          placeholder={SearchPlaceholderText?.value}
                          onKeyDown={numericOnly}
                          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 5 }}
                          error={Boolean(errors?.zipcode)} // Set the error prop based on the presence of an error
                          {...field}
                        />
                        {errors.zipcode && (
                          <Stack style={{ marginTop: '0px' }} alignItems="center" direction="row">
                            <Typography color="error">{`${errors?.zipcode?.message}`}</Typography>
                          </Stack>
                        )}
                      </>
                    )}
                  />
                </Grid>
                <Button
                  style={{
                    height: '40px',
                    padding: '12px 22px 12px 22px',
                    borderRadius: '20px',
                    fontFamily: `${
                      theme?.palette?.primaryFontSemiBoldType || DEFAULT_PRIMARY_SEMIBOLD_FONT
                    }, sans-serif`,
                    fontSize: '16px',
                    fontWeight: '600',
                    lineHeight: '16px',
                    textAlign: 'center',
                    textTransform: 'none',
                    width: '252px',
                  }}
                  variant="contained"
                  onClick={handleZipcodeSearch}
                  type="submit"
                >
                  Search
                </Button>
              </Stack>
            )}
          </Stack>
        </Grid>
      )}
      <Grid item container sx={{ maxHeight: isMobile || isTablet ? '650px' : '550px' }}>
        {/* TODO: Currently sites will get from CDP so there wont be loading also isError flag, 
          so for time being keeping this flag false, once we
          have zipcode story we can modify this logic  */}
        {false ? (
          <Box
            sx={{
              height: '50vh',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <ToggleViewPanel
            bestMatchsitesText={props?.fields?.bestMatchsitesText?.value}
            groupTwoSites={groupTwoSites}
            toggleActivePanel={toggleActivePanel}
            activePannel={activePannel}
            bestMatchSitesIcon={props?.fields?.bestMatchSitesIcon}
            option1={toggleOptions[0]?.fields?.toggleOption}
            option2={toggleOptions[1]?.fields?.toggleOption}
            option1Component={
              data && data.length > 0 ? (
                <SitesList
                  siteDetails={data}
                  buttonText={buttonText}
                  siteAddressFormat={siteAddressFormat}
                  siteNameFormat={siteNameFormat}
                  sitePhoneNumber={sitePhoneNumber}
                  callCenterDefaultNumber={
                    (sitecoreContext?.callcenter as any)?.callCenterDefaultValue?.value
                  }
                  mapIcon={props?.fields?.mapIcon}
                  phoneIcon={props?.fields?.phoneIcon}
                  isError={false}
                  virtualIcon={props?.fields?.virtualIcon}
                  virtualMapIcon={props?.fields?.virtualMapIcon}
                  virutalSiteText={props?.fields?.virutalSiteText}
                  shouldShowViewMore={showViewButton ? shouldShowViewMore.toString() : 'NotShow'}
                  viewAllSites={viewAllSites}
                  groupTwoSites={groupTwoSites}
                  toggleActivePanel={toggleActivePanel}
                  activePannel={activePannel}
                  viewMoreText={props?.fields?.viewMoreText?.value}
                  viewLessText={props?.fields?.viewLessText?.value}
                  bestMatchsitesText={props?.fields?.bestMatchsitesText?.value}
                  viewGroupOneSites={viewGroupOneSites}
                  premiereSiteIcon={props?.fields?.premiereSiteIcon}
                  bestMatchSitesIcon={props?.fields?.bestMatchSitesIcon}
                  showViewButtonFirstScreen={showViewButtonFirstScreen}
                  showViewButtonSecondScreen={showViewButtonSecondScreen}
                  modalButtonText={modalButtonText}
                />
              ) : (
                noSitesAvailable()
              )
            }
            option2Component={
              <SitesMap
                key={googleMapsLanguage}
                googleMapsLanguage={googleMapsLanguage}
                siteDetails={data}
                fields={props?.fields}
                buttonText={buttonText}
                siteAddressFormat={siteAddressFormatinMap}
                siteNameFormat={siteNameFormatInMap}
                callCenterDefaultNumber={
                  (sitecoreContext?.callcenter as any)?.callCenterDefaultValue?.value
                }
                sitePhoneNumber={sitePhoneNumber}
                mapIcon={props?.fields?.mapIcon}
                phoneIcon={props?.fields?.phoneIcon}
                isError={false}
                modalButtonText={modalButtonText}
              />
            }
            inputProps={{ 'aria-label': props?.fields.switchToggleAriaLabel?.value }}
          />
        )}
      </Grid>
      <Grid
        item
        sx={{
          textAlign: isMobile || isTablet ? 'center' : 'center',
          ...(isMobile && { marginInline: '35px' }),
        }}
      >
        {data &&
          data?.length > 0 &&
          groupTwoSites &&
          groupTwoSites?.length > 0 &&
          activePannel === 'groupOne' && (
            <Box
              sx={{
                background: '#F2F2F2',
                padding: '8px 18px',
                borderRadius: '5px',
                textAlign: 'left',
                color: '#333333',
                fontSize: '1rem',
                fontWeight: '600',
              }}
            >
              <Typography
                sx={{
                  background: '#F2F2F2',
                  padding: '8px 18px',
                  fontWeight: '600',
                  display: 'inline-flex',
                  marginRight: '10px',
                  verticalAlign: 'middle',
                }}
              >
                {props?.fields?.showOtherOptionsText?.value}
              </Typography>
              {isToapplyV2Theme ? (
                <ButtonPrimary
                  aria-label={props.cardButtonAriaLabel}
                  onClick={toggleActivePanel}
                  className="btn"
                >
                  {typeof props?.fields?.showOtherOptionsButton?.value === 'number' ||
                  props?.fields?.showOtherOptionsButton?.value === undefined
                    ? String(props?.fields?.showOtherOptionsButton?.value || '')
                    : props?.fields?.showOtherOptionsButton?.value}
                </ButtonPrimary>
              ) : (
                <UIButton
                  className="btn"
                  onClick={toggleActivePanel}
                  variant="contained"
                  sx={{
                    marginTop: '5px',
                    padding: '5px 10px',
                    background: '#fff',
                    color: (theme) => theme.palette.primary.main,
                    border: 'none',
                    marginLeft: '0',
                  }}
                >
                  <Typography sx={{ fontWeight: '600' }}>
                    {props?.fields?.showOtherOptionsButton?.value}
                  </Typography>
                </UIButton>
              )}
            </Box>
          )}
        {data &&
          data?.length > 0 &&
          !enableSingleScreen &&
          (activePannel === 'all' || activePannel === 'groupTwo') && (
            <Box
              sx={{
                background: '#F2F2F2',
                padding: '8px 18px',
                borderRadius: '5px',
                textAlign: 'left',
                color: '#333333',
                fontSize: '1rem',
                fontWeight: '600',
                display: 'block',
                marginTop: '30px',
              }}
            >
              <Typography
                sx={{
                  fontWeight: '600',
                  display: 'inline-flex',
                  marginRight: '10px',
                  verticalAlign: 'middle',
                }}
              >
                {props?.fields?.noSitesConvenientText?.value}
              </Typography>
              {isToapplyV2Theme ? (
                <ButtonPrimary
                  aria-label={props.cardButtonAriaLabel}
                  onClick={handleClickOnNSCLink}
                  className="btn"
                >
                  {typeof props?.fields?.noSitesConvenientButton?.value === 'number' ||
                  props?.fields?.noSitesConvenientButton?.value === undefined
                    ? String(props?.fields?.noSitesConvenientButton?.value || '')
                    : props?.fields?.noSitesConvenientButton?.value}
                </ButtonPrimary>
              ) : (
                <UIButton
                  className="btn"
                  onClick={handleClickOnNSCLink}
                  variant="contained"
                  sx={{
                    marginTop: '5px',
                    padding: '5px 10px',
                    background: '#fff',
                    color: (theme) => theme.palette.primary.main,
                    border: 'none',
                    width: 'auto',
                    minWidth: '100px',
                  }}
                >
                  <Typography sx={{ fontWeight: '600' }}>
                    {props?.fields?.noSitesConvenientButton?.value}
                  </Typography>
                </UIButton>
              )}
            </Box>
          )}
        {data && data?.length > 0 && enableSingleScreen && (
          <Box
            sx={{
              background: '#F2F2F2',
              padding: '12px 18px',
              borderRadius: '5px',
              textAlign: 'left',
              color: '#333333',
              fontSize: '1rem',
              fontWeight: '600',
            }}
          >
            <Typography
              sx={{
                fontWeight: '600',
                display: 'inline-flex',
                marginRight: '10px',
                verticalAlign: 'middle',
              }}
            >
              {props?.fields?.noSitesConvenientText?.value}
            </Typography>
            {isToapplyV2Theme ? (
              <ButtonPrimary
                aria-label={props.cardButtonAriaLabel}
                onClick={handleClickOnNSCLink}
                className="btn"
              >
                {typeof props?.fields?.noSitesConvenientButton?.value === 'number' ||
                props?.fields?.noSitesConvenientButton?.value === undefined
                  ? String(props?.fields?.noSitesConvenientButton?.value || '')
                  : props?.fields?.noSitesConvenientButton?.value}
              </ButtonPrimary>
            ) : (
              <UIButton
                className="btn"
                onClick={handleClickOnNSCLink}
                variant="contained"
                sx={{
                  marginTop: '5px',
                  padding: '5px 10px',
                  background: '#fff',
                  color: (theme) => theme.palette.primary.main,
                  border: 'none',
                  width: 'auto',
                  minWidth: '100px',
                }}
              >
                <Typography sx={{ fontWeight: '600' }}>
                  {props?.fields?.noSitesConvenientButton?.value}
                </Typography>
              </UIButton>
            )}
          </Box>
        )}
      </Grid>
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: (theme: { zIndex: { drawer: number } }) => theme.zIndex.drawer + 1,
        }}
        open={isRedirecting}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Grid>
  );
};
export default SiteSelection;
